import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { withRouter, RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';

import star from 'four-nets/icons/svg/icon_star-full.svg';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import Styled from 'styled';
import ScrollView from 'lib/scroll/scroll-view';

import arrow from 'four-nets/icons/svg/icon_arrow-left.svg';
import CommentView, { CommentViewModel } from 'four-nets/common/comment/comment-view';
import ActionButtonView from 'four-nets/ui/action-button/action-button-view';

import { Buyer, Product } from 'four-nets/bazaar/bazaar';

import CommentHeaderView from 'four-nets/bazaar/product/negotiation/product-comment-header-view';
import actionButtonStyles from 'four-nets/bazaar/product/detail/product-actions-bar-button-styles.scss';
import defaultStyles from './product-messages-view.scss';

const DEFAULT_SHOWN_COUNT = 3;

export interface Props extends RouteComponentProps {
    styles: typeof defaultStyles;
    buyer: Buyer;
    product: Product;
    loading?: boolean;
    isAuthor: boolean;
    openReview?: () => void;
}

export interface State {
    showAll: boolean;
    showIndex: number;
}

class ProductMessagesView extends React.Component<Props & WithRouter<any>, State> {
    static contextTypes = {
        scrollView: PropTypes.object,
    };

    state = {
        showAll: false,
        showIndex: Math.max(DEFAULT_SHOWN_COUNT, (this.props.buyer.newMessagesCount || 0) + 1),
    };
    ref: any;

    componentDidMount() {
        this.scrollToMessages();
    }

    componentDidUpdate() {
        this.scrollToMessages();
    }

    messages = () => {
        const { buyer, product } = this.props;
        if (!buyer.messages) {
            return [];
        }
        return buyer.messages.map(message => {
            if (message.isSeller) {
                message.user = product.user;
            } else {
                message.user = buyer.user || {
                    avatarUrl: '',
                    username: buyer.email!,
                    isAnonymous: true,
                    dateJoined: new Date(),
                    heartsCount: 0,
                    isSuperstaff: false,
                };
            }
            message.userStore = buyer.userStore;
            return message;
        });
    };

    commentViewModels = () => {
        let messages = this.messages();
        if (!this.state.showAll) {
            messages = messages.slice(-this.state.showIndex);
        }
        return messages.map(comment => new CommentViewModel({ comment }));
    };

    scrollToMessages = () => {
        if (this.ref) {
            this.context.scrollView.scrollToRef({
                ref: this.ref,
                duration: 300,
                delay: 40,
                offset: -90,
            });
        }
    };

    setRef = (ref: any) => {
        this.ref = ref;
    };

    showAll = () => {
        this.setState({
            showAll: true,
        });
    };

    showMoreIsHidden = () => {
        const enoughShown = this.state.showIndex >= this.messages().length;
        return this.state.showAll || enoughShown;
    };

    render() {
        const { buyer, isAuthor, product } = this.props;
        const styles = this.props.styles!;

        return (
            <div ref={this.setRef}>
                {isAuthor ? (
                    <Link to={product.href} className={styles.backBar}>
                        <SVGIcon style={{ image: styles.icon }} src={arrow} />
                        <FormattedMessage id='BAZAAR_BACK_TO_BUYERS' />
                    </Link>
                ) : null}
                <div className={styles.titleWrapper}>
                    <h2 className={styles.title}>
                        <FormattedMessage id='BAZAAR_CONVERSATION' />
                    </h2>
                    {this.props.openReview ? (
                        <span>
                            <ActionButtonView
                                styles={actionButtonStyles}
                                onClick={this.props.openReview}
                                icon={<SVGIcon style={{ image: styles.icon }} src={star} />}
                            >
                                {this.props.isAuthor ? (
                                    <FormattedMessage
                                        id='REVIEW_BUYER'
                                        values={{
                                            gender: '',
                                            gender2: 'm2',
                                        }}
                                    />
                                ) : (
                                    <FormattedMessage
                                        id='REVIEW_SELLER'
                                        values={{
                                            gender: '',
                                            gender2: 'm2',
                                        }}
                                    />
                                )}
                            </ActionButtonView>
                        </span>
                    ) : null}
                </div>
                {this.showMoreIsHidden() ? null : (
                    <div className={styles.showAllWrapper}>
                        <button className={styles.showAll} onClick={this.showAll}>
                            <FormattedMessage id='BAZAAR_SHOW_ALL_MESSAGES' />
                        </button>
                    </div>
                )}
                <ul className={styles.unorderedList}>
                    {this.commentViewModels().map(commentViewModel => (
                        <li key={commentViewModel.comment.id} className={styles.listItem}>
                            <CommentView
                                key={commentViewModel.comment.id}
                                commentViewModel={commentViewModel}
                                customHeader={
                                    <CommentHeaderView
                                        commentHeaderViewModel={
                                            commentViewModel.commentBodyViewModel.commentHeaderViewModel
                                        }
                                        rating={buyer.rating}
                                    />
                                }
                                customFooter={null}
                            />
                        </li>
                    ))}
                </ul>
                {isAuthor ? (
                    <Link className={styles.backBarBottom} to={product.href}>
                        <SVGIcon style={{ image: styles.icon }} src={arrow} />
                        <FormattedMessage id='BAZAAR_BACK_TO_BUYERS' />
                    </Link>
                ) : null}
                {this.props.children}
            </div>
        );
    }
}

export default Styled(defaultStyles)(ProductMessagesView);
